/**
 * Shared input mapping + validation for the counter/hedge PRICE LADDER pair of
 * fields (`counterHedgeLadderStepMs`, `counterHedgeLadderAdverseTicks`).
 *
 * The engine walks the counter/hedge leg through three combined-sum targets —
 * the rule threshold, break-even at 1.00, and the pre-authorised
 * `marketHedgeMaxSum` — advancing one rung every `counterHedgeLadderStepMs`
 * milliseconds instead of resting a single order at the ceiling.
 *
 * A RESOLVED `counterHedgeLadderStepMs == null` is the gate: there is no
 * separate toggle, and the field carries no default anywhere in the chain. An
 * EMPTY input is never `0` — `0` is rejected here so an operator who typed it
 * is told, instead of being quietly served by a different mechanism. A set
 * value must be an integer at or above `COUNTER_LADDER_MIN_STEP_MS`.
 *
 * 🚨 What an empty input MEANS depends on the SCOPE that renders it, and these
 * parsers are shared by three of them:
 *   - **group** (`CreateGroupDialog`) — blank is the ladder OFF for every rule
 *     that inherits: the group is the bottom of the `rule ?? group` chain;
 *   - **rule** (the opener row's `Ladd` cell, and the bulk bar, which writes
 *     rule documents) — blank is INHERIT. The engine resolves `rule ?? group`,
 *     so an empty cell under a walking group WALKS. There is no rule-level way
 *     to say "this rule does not walk";
 *   - **subgroup** (`SubgroupConfigDialog`) — blank omits the key from the
 *     snapshot, so Apply LEAVES MEMBER RULES ALONE. It changes nothing at all.
 *
 * `counterHedgeLadderAdverseTicks` accepts a real `0`: it disables the
 * adverse-move branches while leaving the timer ladder running. ⚠️ Its default
 * is **0**, not the 2 the spec asked for — `COUNTER_LADDER_ADVERSE_TICKS_DEFAULT`
 * shipped at 0 (`position-engine-rs/crates/contracts/src/rule.rs`), so turning
 * `stepMs` on gives the TIMED ladder with the signal OFF and the adverse skip
 * has to be asked for explicitly. And do NOT read a default off the engine's
 * existing `adverse_cancel_ticks` (`rule.rs`, `adverse_cancel_ticks_resolved()`):
 * that is the maker adverse-CANCEL threshold, a different mechanism that
 * happens to default to 2.
 *
 * Both parsers map blank to `null`. What `null` MEANS on the wire is the
 * caller's decision, and the two group dialogs deliberately disagree:
 * `CreateGroupDialog` sends an explicit `null` on edit (clear the group
 * fallback) and omits the key on create, while `SubgroupConfigDialog` omits the
 * key from its snapshot so Apply leaves member rules untouched.
 */

/**
 * Hard floor on the rung interval, from the spec
 * (`arbitrage-core/docs/specs/2026-08-18-opener-counter-hedge-price-ladder.md`):
 * below the venues' own cancel + confirm + create round trip the extra rungs
 * are not faster, just extra venue load.
 *
 * ⚠️ This note used to say the backend knew nothing about these two fields.
 * That is out of date: `arbitrage-core@main` now carries
 * `COUNTER_HEDGE_LADDER_MIN_STEP_MS = 250` on all THREE write paths — `@Min`
 * on the create DTO, inherited by the update via `PartialType`, and again on
 * the bulk-params DTO — plus the engine itself, which CLAMPS a sub-floor
 * interval UP rather than honouring it. (The resolver in
 * `rule-definition.mapper.ts` carries the FIELD, deliberately not the floor.) So the 400 is real, and this
 * FE check is no longer the only thing standing between an operator and a
 * sub-floor value; it is what turns that 400 into a red box before the write.
 */
export const COUNTER_LADDER_MIN_STEP_MS = 250;

/** Section heading both group dialogs put the two fields under. */
export const LADDER_SECTION_LABEL = 'Hedge ladder';
export const LADDER_STEP_MS_LABEL = 'Step (ms)';
export const LADDER_ADVERSE_TICKS_LABEL = 'Adverse (ticks)';

/**
 * ⚠️ Rendered at THREE scopes and only one of them can call blank an "off"
 * (see the module docstring): the group dialog, the subgroup dialog — where
 * blank leaves member rules untouched — and the bulk bar, which writes rule
 * documents. So the text says what blank DOES here (clears this level's value)
 * and leaves what that MEANS to the scope's own help string.
 *
 * `counterHedgeLadder.test.ts` pins only that it contains "blank", so a
 * reword that drops the word stays green — keep it.
 */
export const LADDER_STEP_MS_ERROR = `Must be an integer ≥ ${COUNTER_LADDER_MIN_STEP_MS} ms — or blank to clear this level's interval`;
export const LADDER_ADVERSE_TICKS_ERROR = 'Must be an integer ≥ 0';

/**
 * The two things an operator cannot read off the fields themselves, and both
 * cost real money to learn the hard way: the ladder is inert under any hedge
 * mode other than `gtc-to-max-sum`, and the interval is read when a unit is
 * SPAWNED, so flipping it does nothing to the cycles already running. (Per the
 * spec — see the note on the help constants below.)
 */
export const LADDER_STEP_MS_HELP =
  'Counter/hedge ladder: walk the hedge across threshold → 1.00 → max sum, one rung every N ms. Blank here = off for every rule that inherits (in the subgroup dialog, blank instead leaves member rules untouched). Only does anything on gtc-to-max-sum; on/off takes effect at the next unit spawn, not on units already running.';

/**
 * ⚠️ These two strings, unlike the comments in this file, describe what the
 * FEATURE does once the engine ships it — that is their job, and the two facts
 * they carry (gtc-to-max-sum only, next unit spawn) are the ones an operator
 * cannot infer from the fields. They are deliberately NOT hedged with "specced";
 * the deploy order is backend-first, so by the time this text is read in
 * production the behaviour it describes exists.
 */
/**
 * 🚨 Blank resolves to **0**, not to the spec's 2:
 * `COUNTER_LADDER_ADVERSE_TICKS_DEFAULT` shipped as 0
 * (`position-engine-rs/crates/contracts/src/rule.rs`). Turning `stepMs` on
 * therefore gives the TIMED ladder with the signal OFF, and the adverse skip
 * has to be asked for explicitly. This string said "the default of 2" and sent
 * an operator away believing they had a skip they did not have.
 */
export const LADDER_ADVERSE_TICKS_HELP =
  'Ticks the maker leg’s own book must move for the ladder to skip straight to max sum (adverse) or raise the appreciation alert (favourable). Blank here = the engine default, which ships at 0 = both branches OFF (in the subgroup dialog, blank instead leaves member rules untouched). An explicit 0 says the same thing out loud; the timed ladder still advances either way.';

/**
 * A parsed ladder field. `error` non-null means the operator typed something
 * invalid — `value` is then `null` and callers must NOT ship it, since `null`
 * on the wire is a meaningful value (off / leave alone) rather than "ignore me".
 */
export interface ParsedLadderField {
  value: number | null;
  error: string | null;
}

/**
 * Raw input string → `counterHedgeLadderStepMs`. Empty = `null`, which is the
 * ladder's absence at THIS level — see the scope note in the module docstring
 * before reading that as "no ladder".
 */
export function parseLadderStepMsInput(raw: string): ParsedLadderField {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, error: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < COUNTER_LADDER_MIN_STEP_MS) {
    return { value: null, error: LADDER_STEP_MS_ERROR };
  }
  return { value: n, error: null };
}

/**
 * Raw input string → `counterHedgeLadderAdverseTicks`. Empty = `null`, which
 * resolves `rule ?? group ?? 0` — the engine's shipped default is **0**, not
 * the 2 the spec asked for, so a blank cell under a group that sets 3 runs the
 * adverse skip at 3. `0` is accepted and means "disable the branches".
 */
export function parseLadderAdverseTicksInput(raw: string): ParsedLadderField {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, error: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) {
    return { value: null, error: LADDER_ADVERSE_TICKS_ERROR };
  }
  return { value: n, error: null };
}

/** Rule/group/subgroup field → input string (`null`/absent renders as empty). */
export function ladderFieldToInput(value: number | null | undefined): string {
  return value != null ? String(value) : '';
}
